import * as React from 'react';
import { RegisterOptions } from 'react-hook-form';
import { toast } from 'react-toastify';
import ReCAPTCHA from 'react-google-recaptcha';
import styled from 'styled-components';
import Option from '../../models/Option';
import GenericForm from '../common/rhf/GenericForm';
import { FormContent, HalfCell, Row } from '../common/FormComponents';
import SubSection from '../../commonComponents/fields/SubSection';
import Text from '../common/rhf/Text';
import DropDownList from '../common/rhf/DropDownList';
import Customer, { Registration } from '../../models/be/Customer';
import { createCustomerRegistration } from '../../core/coreApi';
import {
	ukCountryCode,
	auCountryCode,
	ukPostCodeRegex,
	auPostCodeRegex,
	ukPostCodeRegexErrorMessage,
	auPostCodeRegexErrorMessage,
	nzlCountryCode,
	irlCurrencyCode,
	sgCountryCode,
	caCountryCode,
	usCountryCode
} from '../../core/constants';
import {
	getIsoCurrencyCodeForCountryCode,
	getCurrencySymbolUsingIsoCurrencyCode
} from '../../core/internationalisation';
import { getFullCountryList } from '../../core/countryList';
import { alphanumericRegexErrorMessage, alphanumericWithSpacesRegexString } from '../../core/domain';
import CustomerNameField from '../common/rhf/CustomerNameField';
import Email from '../common/rhf/Email';
import MobileNumber from '../common/rhf/MobileNumber';
import { CheckboxInput } from '../common/fields/Checkbox';
import FieldDecorator from '../../commonComponents/fields/FieldDecorator';
import { recaptchaSiteKey } from '../../config.local';
import { sendAccountSetupEmail } from '../../core/api';

const ClientRegistrationForm = () => {
	const [recaptchaValue, setRecaptchaValue] = React.useState<string | null>(null);
	const [isDisabled, setIsDisabled] = React.useState<boolean>(true);
	const [policyChecked, setPolicyChecked] = React.useState<boolean>(false);
	const [termsChecked, setTermsChecked] = React.useState<boolean>(false);

	React.useEffect(() => {
		setIsDisabled(!(policyChecked && termsChecked && recaptchaValue));
	}, [policyChecked, termsChecked, recaptchaValue]);

	const handleRecaptchaChange = (value: string) => {
		setRecaptchaValue(value);
	};

	const handleFormSubmit = async (data: Registration) => {
		try {
			await createCustomerRegistration(data);
			await sendAccountSetupEmail(data);
			toast('Registration successful!');
		} catch (error) {
			toast('An error occurred during registration. Please try again.');
		}
	};

	const getPattern = (values: { countryISOCode: string }) => {
		if (values.countryISOCode === ukCountryCode)
			return { value: new RegExp(ukPostCodeRegex), message: ukPostCodeRegexErrorMessage };
		if (values.countryISOCode === auCountryCode)
			return { value: new RegExp(auPostCodeRegex), message: auPostCodeRegexErrorMessage };
		return {
			value: new RegExp(alphanumericWithSpacesRegexString),
			message: alphanumericRegexErrorMessage
		};
	};

	return (
		<>
			<PageTitle>
				<h1>Client Registration</h1>
			</PageTitle>
			<FormContainer>
				<FormContent>
					<GenericForm
						defaultValues={null}
						onSubmission={handleFormSubmit}
						showDeleteButton={false}
						isShownSecondaryButton={false}
						primaryButtonText="Submit"
						data-hs-cf-bound="false"
						buttonsCentre
						primaryButtonDisabled={isDisabled}
					>
						<SubSection title="Client Details" collapsable>
							<Row>
								<HalfCell>
									<CustomerNameField fieldName="customerName" label="Company Name" required autoFocus />
								</HalfCell>
								<HalfCell>
									<CustomerNameField fieldName="publicDisplayName" label="Public Display Name" />
								</HalfCell>
							</Row>
							<Row>
								<HalfCell>
									<Text fieldName="addressLine1" label="Addresss Line 1" required />
								</HalfCell>
								<HalfCell>
									<Text fieldName="addressLine2" label="Addresss Line 2" />
								</HalfCell>
							</Row>
							<Row>
								<HalfCell>
									<Text fieldName="town" label="Suburb/Town" required />
								</HalfCell>
								<HalfCell>
									<Text fieldName="county" label="County/State" required />
								</HalfCell>
							</Row>
							<Row>
								<HalfCell>
									<DropDownList
										fieldName="countryISOCode"
										label="Country"
										dataValues={getFullCountryList()}
										required
										width="100%"
									/>
								</HalfCell>
								<HalfCell>
									<Text
										fieldName="postalCode"
										label="Postal Code"
										required
										createRules={(values: Customer): Partial<RegisterOptions> => ({
											pattern: getPattern(values)
										})}
									/>
								</HalfCell>
							</Row>
							<Row>
								<HalfCell>
									<DropDownList
										fieldName="currencyISOCode"
										label="Currency"
										dataValues={getCurrencyOptions()}
										required
										width="97%"
									/>
								</HalfCell>
							</Row>
						</SubSection>
						<SubSection title="Primary User Details" initiallyClosed={false}>
							<Row>
								<HalfCell>
									<Text fieldName="firstName" label="First Name" required maxLength={50} />
								</HalfCell>
								<HalfCell>
									<Text fieldName="lastName" label="Last Name" required maxLength={50} />
								</HalfCell>
							</Row>
							<Row>
								<HalfCell>
									<Email fieldName="emailAddress" label="Email" required />
								</HalfCell>
								<HalfCell>
									<MobileNumber fieldName="phoneNumber" />
								</HalfCell>
							</Row>
							<Row>
								<HalfCell>
									<CheckboxInput
										value={policyChecked}
										onValueChange={(_fieldName, value: boolean) => {
											setPolicyChecked(value);
										}}
										name="policy"
									/>
									<label className="k-checkbox-label" htmlFor="policy">
										I have read and agreed to the{' '}
										<a href="/privacy-policy" target="_blank" rel="noreferrer">
											Privacy Policy
										</a>
									</label>
								</HalfCell>
								<HalfCell>
									<CheckboxInput
										value={termsChecked}
										onValueChange={(_fieldName, value: boolean) => {
											setTermsChecked(value);
										}}
										name="terms"
									/>
									<label className="k-checkbox-label" htmlFor="terms">
										I have read and agreed to the{' '}
										<a href="/terms-and-conditions" target="_blank" rel="noreferrer">
											Terms & Conditions
										</a>
									</label>
								</HalfCell>
							</Row>
							<Row>
								<RecaptchaContainer>
									<FieldDecorator className="recaptcha">
										<ReCAPTCHA sitekey={recaptchaSiteKey} onChange={handleRecaptchaChange} ref={null} />
									</FieldDecorator>
								</RecaptchaContainer>
							</Row>
						</SubSection>
					</GenericForm>
				</FormContent>
			</FormContainer>
		</>
	);
};

export default ClientRegistrationForm;

const getCurrencyOptions = (): Option<string>[] => {
	return [auCountryCode, irlCurrencyCode, ukCountryCode, nzlCountryCode, sgCountryCode, caCountryCode, usCountryCode]
		.sort((a, b) => a.localeCompare(b))
		.map((countryCode) => {
			const isoCurrencyCode = getIsoCurrencyCodeForCountryCode(countryCode);
			const twoLetterCountryCodeEndIndex = 2;
			const text = `${countryCode.slice(0, twoLetterCountryCodeEndIndex)}${getCurrencySymbolUsingIsoCurrencyCode(
				isoCurrencyCode
			)}`;
			return { text, value: isoCurrencyCode };
		});
};

const PageTitle = styled.div`
	position: fixed;
	width: 100%;
	height: 104px;
	background-color: #fff;
	padding: 28px 40px 28px 100px;
	z-index: 100;
	border-bottom: solid 1px rgba(38, 38, 38, 0.2);
`;

const FormContainer = styled.div`
	width: 100%;
	background-color: #fff;
	padding: 100px 40px 28px 100px;
	z-index: 100;
`;

const RecaptchaContainer = styled.div`
	margin-left: 220px;
`;
